import type { ProofElement, ProofSection } from '@/components/proof/types';

/** A single image aggregated into the risk-evidence Images tab. `src` may be a
 *  data URI, an absolute URL, or a storage key — resolution is handled by
 *  useResolvedImageSrc when the image is rendered. */
export interface RiskImage {
  src: string;
  caption?: string;
  alt?: string;
  registryKey?: string;
}

function collectFromElements(
  elements: ProofElement[] | undefined,
  out: RiskImage[],
  seen: Set<string>
): void {
  if (!elements) return;
  for (const element of elements) {
    if (element.type === 'image' && element.image) {
      const src = element.image.data_uri || element.image.url;
      // Dedupe by `src` so an image cited on multiple demonstrated-chain steps
      // yields a single thumbnail; distinct srcs are all kept.
      if (src && !seen.has(src)) {
        seen.add(src);
        out.push({
          src,
          caption: element.image.caption,
          alt: element.image.alt_text,
        });
      }
    }
    // Images can be nested inside `group` elements (e.g. the demonstrated-chain
    // steps emitted by the backend), so recurse into groups too.
    if (element.group?.elements) {
      collectFromElements(element.group.elements, out, seen);
    }
  }
}

/** Collects every image element (including images nested inside demonstrated-chain
 *  groups) from a structured proof's sections into a flat RiskImage list,
 *  deduplicated by `src`. */
export function collectRiskImages(sections: ProofSection[]): RiskImage[] {
  const images: RiskImage[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    collectFromElements(section.elements, images, seen);
  }
  return images;
}
